import moment from 'moment'

import { getDateNow, getTimeNow, getTimeDifference } from '@/utils/helpers'

import { BUSINESS_DAYS_TO_ROWS } from '@/constants'

export default {
	getExistingToGoHours() {
		return (
			this.toGoHours &&
			this.toGoHours.length &&
			this.toGoHours.filter(item => !item.closed)
		)
	},

	checkIsAvailable(venueInfo) {
		this.venueInfo = venueInfo
		this.timeZone = venueInfo.timeZone
		this.toGoHours = venueInfo.toGoHours
		const { acceptOnlineOrder } = venueInfo
		const isOldVersion = !this.toGoHours.length

		if (isOldVersion) {
			return acceptOnlineOrder
		} else {
			const existingToGoHours = this.getExistingToGoHours()
			const hasExistingToGoHours = !!existingToGoHours.length
			const isPrevDayHoursEnabled = this.checkIsPrevDayHoursEnabled()
			const isCurDayHoursEnabled = this.checkIsCurDayHoursEnabled()

			return (
				hasExistingToGoHours && (isPrevDayHoursEnabled || isCurDayHoursEnabled)
			)
		}
	},

	// Remove from v.4.2
	// getDaysShift() {
	// 	const { weekStart } = this.venueInfo.businessDays
	//
	// 	if (!weekStart) {
	// 		return 0
	// 	} else {
	// 		return WEEK_DAYS.findIndex(day => {
	// 			return day.short.toLowerCase() === weekStart.toLowerCase()
	// 		})
	// 	}
	// },

	getIndexOfDow(date) {
		const nowDayIndex = date.day()
		let index = nowDayIndex + 1

		if (index > 7) {
			index -= 7
		} else if (index < 1) {
			index += 7
		}

		return index
	},

	getNextAvailableToday(curTime, isPrevDay, indexOfDow, endOfBusinessDay) {
		const curBusinessDayPeriods = this.getDayAllToGoHours(indexOfDow)

		return curBusinessDayPeriods.filter(period => {
			const { startTime, endTime } = period
			// 24 hours period isn't available
			if (startTime === endTime || startTime === endOfBusinessDay) return false

			// Previous business day
			if (isPrevDay) {
				// Previous business day, but today according the calendar
				if (endTime <= endOfBusinessDay) {
					return startTime < endTime && startTime > curTime
				}

				return false
			}
			// Current business day and current day according the calendar
			if (startTime > endOfBusinessDay) {
				return curTime < startTime
			}
			// Current business day but next day according the calendar
			return curTime > startTime
		})
	},

	getCurrentPeriod(curTime, isPrevDay, curDow, endOfBusinessDay) {
		let dayAllToGoHours = this.toGoHours.filter(hoursObj => {
			if (!(hoursObj.dow === curDow && !hoursObj.closed)) return false

			const { startTime, endTime } = hoursObj
			// Current business day
			const checkIsExist = () => {
				// 24 hours period is always available
				if (startTime === endTime) {
					return true
				}
				// Period ends today
				if (startTime < endTime) {
					return startTime <= curTime && curTime <= endTime
				}
				// Period ends on the next day
				if (startTime > endTime) {
					return curTime >= startTime
				}
			}
			// Previous business day
			const checkIsExistPrevDay = () => {
				// 24 hours period is always available
				if (startTime === endTime) {
					return true
				}
				// Period ended on the day before
				if (endTime > endOfBusinessDay) return false
				// Period started on the day before
				if (startTime >= endOfBusinessDay) {
					return curTime < endTime
				}
				// Period started today
				return startTime <= curTime && curTime <= endTime
			}

			const isPeriodExistCurTime = isPrevDay
				? checkIsExistPrevDay()
				: checkIsExist()
			return hoursObj && isPeriodExistCurTime
		})

		dayAllToGoHours = dayAllToGoHours.sort((dayA, dayB) => {
			return dayA.position - dayB.position
		})

		return dayAllToGoHours
	},

	getDayAllToGoHours(indexOfDow) {
		let dayAllToGoHours = this.toGoHours.filter(hoursObj => {
			return hoursObj.dow === indexOfDow && !hoursObj.closed
		})

		if (!dayAllToGoHours.length) return []

		dayAllToGoHours = dayAllToGoHours.sort((dayA, dayB) => {
			return dayA.position - dayB.position
		})

		return dayAllToGoHours
	},

	getClosestAvailableTime(venueInfo) {
		this.venueInfo = venueInfo
		this.timeZone = venueInfo.timeZone
		this.toGoHours = venueInfo.toGoHours
		// Date and time according venue timezone
		const { nowDate } = this.getDateTimeNow()
		// Current time in the venue
		const curTime = nowDate.format('HH:mm:ss')
		const { businessDays } = this.venueInfo
		// Current day of the week number
		const indexOfDow = this.getIndexOfDow(moment(nowDate))

		let curDow = indexOfDow
		let endOfBusinessDay = businessDays[BUSINESS_DAYS_TO_ROWS[indexOfDow]]
		// Need compare with previous business day
		const isPrevDay = curTime < endOfBusinessDay

		if (isPrevDay) {
			// Previous day of the week number
			curDow = indexOfDow === 1 ? 7 : indexOfDow - 1
			// End of the previous business day
			endOfBusinessDay = businessDays[BUSINESS_DAYS_TO_ROWS[curDow]]
		}

		const currentPeriods = this.getCurrentPeriod(
			curTime,
			isPrevDay,
			curDow,
			endOfBusinessDay
		)

		if (currentPeriods.length) {
			return {
				periods: currentPeriods,
				isToGoNotAvailableNowAndAvailableToday: false
			}
		}

		const nextAvailableToday = this.getNextAvailableToday(
			curTime,
			isPrevDay,
			curDow,
			endOfBusinessDay
		)

		if (nextAvailableToday.length) {
			return {
				periods: nextAvailableToday,
				isToGoNotAvailableNowAndAvailableToday: true
			}
		}

		return {
			periods: this.getNextAvailableTime(isPrevDay),

			isToGoNotAvailableNowAndAvailableToday: false
		}
	},

	getDateTimeNow() {
		const nowDate = getDateNow(this.timeZone)
		const nowTime = getTimeNow(this.timeZone)

		return {
			nowDate,
			nowTime
		}
	},

	checkIsPrevDayHoursEnabled() {
		const { nowDate, nowTime } = this.getDateTimeNow()
		const previousBusinessDay = moment(nowDate).subtract(1, 'days')
		const indexOfDow = this.getIndexOfDow(previousBusinessDay)
		const dayAllToGoHours = this.getDayAllToGoHours(indexOfDow)

		if (!dayAllToGoHours.length) return false

		const firstStartTime = dayAllToGoHours[0].startTime

		for (let i = 0; i < dayAllToGoHours.length; i++) {
			const { startTime, endTime } = dayAllToGoHours[i]
			const isFirstDayStarts = getTimeDifference(firstStartTime, startTime) <= 0
			const isTransition = getTimeDifference(endTime, startTime) <= 0

			if (
				(isTransition && getTimeDifference(nowTime, endTime) <= 0) ||
				(!isFirstDayStarts &&
					getTimeDifference(nowTime, startTime) >= 0 &&
					getTimeDifference(nowTime, endTime) <= 0)
			) {
				return true
			}
		}

		return false
	},

	checkIsCurDayHoursEnabled() {
		const { nowDate, nowTime } = this.getDateTimeNow()
		const indexOfDow = this.getIndexOfDow(moment(nowDate))
		const dayAllToGoHours = this.getDayAllToGoHours(indexOfDow)

		if (!dayAllToGoHours.length) return false

		const firstStartTime = dayAllToGoHours[0].startTime

		for (let i = 0; i < dayAllToGoHours.length; i++) {
			const { startTime, endTime } = dayAllToGoHours[i]
			const isFirstDayStarts = getTimeDifference(firstStartTime, startTime) <= 0
			const isTransition = getTimeDifference(endTime, startTime) <= 0

			if (
				(isTransition && getTimeDifference(nowTime, startTime) >= 0) ||
				(isFirstDayStarts &&
					!isTransition &&
					getTimeDifference(nowTime, startTime) >= 0 &&
					getTimeDifference(nowTime, endTime) <= 0)
			) {
				return true
			}
		}

		return false
	},

	getNextAvailableTime(isPrevDay) {
		const { nowDate } = this.getDateTimeNow()
		let nextDate = isPrevDay ? moment(nowDate) : moment(nowDate).add(1, 'day')

		for (let i = 0; i < 7; i++) {
			const indexOfDow = this.getIndexOfDow(moment(nextDate))
			let nextIndex = indexOfDow + i

			if (nextIndex > 7) nextIndex = nextIndex - 7

			const dayAllToGoHours = this.getDayAllToGoHours(nextIndex)

			if (!dayAllToGoHours.length) continue

			const firstTime = dayAllToGoHours[0].startTime
			const secondTime = dayAllToGoHours[dayAllToGoHours.length - 1].endTime
			const dow = dayAllToGoHours[0].dow

			if (firstTime && secondTime) {
				const data = {
					startTime: firstTime,
					endTime: secondTime,
					dow
				}

				return [data]
			}
		}
	}
}
